import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiExternalLink } from 'react-icons/fi';

import { Release_1_0 } from '../releases/Release_1_0';
import { Release_2_0 } from '../releases/Release_2_0';
import { Release_2_1 } from '../releases/Release_2_1';
import { Release_3_0 } from '../releases/Release_3_0';

const releaseMap = {
  '3.0': <Release_3_0 />,
  '2.1': <Release_2_1 />,
  '2.0': <Release_2_0 />,
  '1.0': <Release_1_0 />,
};

const latest = '3.0'

const ReleaseNote = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(latest);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Overview', href: '/' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Release Note', href: '/release-note' },
    { name: 'Help', href: 'https://cashmate.tawk.help', external: true },
  ];

  return (
    <div className="relative bg-[#0b0812] min-h-screen text-white">

      {/* Navbar (stays on top using absolute) */}
      <nav className="w-full absolute top-0 left-0 right-0 z-50 bg-transparent text-white px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/images/logo.png" alt="CashMate Logo" className="h-10 md:h-12" />
          <span style={{ fontFamily: 'Century Gothic' }} className="text-2xl tracking-[8px]">
              CASHMATE
            </span>
        </div>

        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-white/80">
          <a href="/" className="hover:text-white transition">Overview</a>
          <a href="/pricing" className="hover:text-white transition">Pricing</a>
          <a href="/release-note" className="hover:text-white transition">Release Note</a>
          <a href="https://cashmate.tawk.help" className="hover:text-white transition">Help</a>
          <a href="https://nctechnologyhub.com.my/contact" className="hover:text-white transition">Contact</a>
            <Link to="/login">
              <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="px-5 py-2 text-sm font-semibold tracking-wide text-white bg-white/10 backdrop-blur-md rounded-full border border-white/20 hover:scale-105 transition"
              >
                Sign in
              </motion.button>
            </Link>
          </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden">
          <button onClick={() => setIsOpen(!isOpen)} className="focus:outline-none">
            <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <div className="absolute top-20 left-0 right-0 bg-[#0b0812] shadow-lg text-center px-6 py-8 md:hidden z-40 space-y-6">
            <a href="#overview" className="block text-white/80 hover:text-white transition">Overview</a>
            <a href="/pricing" className="block text-white/80 hover:text-white transition">Pricing</a>
            <a href="/release-note" className="block text-white/80 hover:text-white transition">Release Note</a>
            <a href="https://cashmate.tawk.help" className="block text-white/80 hover:text-white transition">Help</a>
            <a href="https://nctechnologyhub.com.my/contact" className="block text-white/80 hover:text-white transition">Contact</a>
            <Link to="/login">
              <button className="mt-4 w-full px-6 py-2 bg-white/10 text-white border border-white/20 rounded-full hover:scale-105 transition">
                Sign in
              </button>
            </Link>
            </div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-white mb-4">
            Release Notes
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Stay updated with the latest features and improvements
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Version Selector */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
              <h2 className="text-lg font-semibold mb-4 text-white/90">Versions</h2>
              <div className="space-y-2">
                {Object.keys(releaseMap).map((ver) => (
                  <button
                    key={ver}
                    onClick={() => setSelectedVersion(ver)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                      selectedVersion === ver
                        ? 'bg-gradient-to-r from-purple-600/30 to-pink-600/30 text-white border border-purple-500/50 shadow-lg shadow-purple-500/10'
                        : 'text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Version {ver}</span>
                      {selectedVersion === ver && (
                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-400"></div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Release Notes Content */}
          <motion.div 
            key={selectedVersion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex-1 bg-white/5 backdrop-blur-lg rounded-2xl p-6 md:p-8 border border-white/10"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                Version {selectedVersion} Release Notes
              </h2>
              {selectedVersion === latest && (
              <span className="px-3 py-1 text-sm rounded-full bg-gradient-to-r from-purple-600/30 to-pink-600/30 text-purple-200">
                Latest
              </span>
              )}
            </div>
            <div className="prose prose-invert max-w-none">
              {releaseMap[selectedVersion]}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ReleaseNote;
